/**
 * Calls (Oct 2026, design_handoff_messages Part B): the pure half.
 *
 * Phases, labels, caps, the snap-to-corner geometry for the floating window and the one rule
 * that decides who sends the offer. No DOM, no WebRTC — CallProvider owns those and reads this.
 */

export const PHASES = ['perm', 'calling', 'ringing', 'incoming', 'connecting', 'live', 'ended'];

/** A mesh with no SFU: voice up to six people including you, video up to four. */
export const MAX_VOICE = 6;
export const MAX_VIDEO = 4;
/** An invite rings this long before the caller gives up (NO ANSWER) and the callee's screen goes (MISSED). */
export const RING_MS = 30000;
/** Open / close of the portal transition and the snap of the floating window. */
export const PORTAL_OPEN_MS = 560;
export const PORTAL_CLOSE_MS = 400;
export const SNAP_MS = 220;
export const SNAP_EASE = 'cubic-bezier(.2,.7,.2,1)';

export const RING_STOPS = ['#FD199F', '#FF33C6', '#B5B1FF', '#127BFD', '#22ECFB', '#FAE1B4', '#FCAA6D'];
export const RING_GRADIENT = `linear-gradient(90deg, ${[...RING_STOPS, RING_STOPS[0]].join(',')})`;
export const RING_CONIC = `conic-gradient(${[...RING_STOPS, RING_STOPS[0]].join(',')})`;

export const C = {
  live: '#22C55E',
  end: '#EF4444',
  speaker: '#22ECFB',
  cardShadow: '#FD199F',
};

/** `2:14` from a start time, never from accumulated ticks. */
export function fmtTimer(startedAt, now = Date.now()) {
  if (startedAt == null) return '0:00';
  return fmtSeconds((now - startedAt) / 1000);
}

/** `4:32` from a count of seconds. */
export function fmtSeconds(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The status line under the name on the full screen. */
export function statusLabel(call, now = Date.now()) {
  if (!call) return '';
  switch (call.phase) {
    case 'calling': return 'CALLING…';
    case 'ringing': return 'RINGING…';
    case 'connecting': return 'CONNECTING…';
    case 'live': return fmtTimer(call.startedAt, now);
    case 'ended': return call.endedLabel || 'CALL ENDED';
    default: return '';
  }
}

/** `ENCRYPTED · PEER-TO-PEER` / `ENCRYPTED · RELAYED` from the selected candidate pair. */
export function routeLabel(route) {
  return route === 'relayed' ? 'ENCRYPTED · RELAYED' : 'ENCRYPTED · PEER-TO-PEER';
}

/** `GROUP CALL · 3 OF 6` / `GROUP VIDEO · 3 OF 4` */
export function groupTitle(call) {
  const n = (call?.peers?.length || 0) + 1;
  const cap = call?.video ? MAX_VIDEO : MAX_VOICE;
  return `${call?.video ? 'GROUP VIDEO' : 'GROUP CALL'} · ${n} OF ${cap}`;
}

/** The mini bar's line: `REZA · 2:14` or `GROUP CALL · 3 · 2:14`. */
export function miniLabel(call, now = Date.now()) {
  if (!call) return '';
  const t = fmtTimer(call.startedAt, now);
  if ((call.peers?.length || 0) > 1) return `GROUP ${call.video ? 'VIDEO' : 'CALL'} · ${call.peers.length + 1} · ${t}`;
  const first = (call.peers?.[0]?.name || '').split(' ')[0].toUpperCase() || 'CALL';
  return `${first} · ${t}`;
}

/** How many more people the call takes, and the words when it takes none. */
export function roomFor(call) {
  const cap = call?.video ? MAX_VIDEO : MAX_VOICE;
  const n = (call?.peers?.length || 0) + 1;
  return { cap, room: Math.max(0, cap - n), full: `${call?.video ? 'VIDEO' : 'VOICE'} CALLS ARE UP TO ${cap} PEOPLE`, line: `${call?.video ? 'VIDEO' : 'VOICE'} · UP TO ${cap} PEOPLE` };
}

/**
 * Who offers: once two peers both know about each other, the one with the smaller id sends
 * the SDP offer. Symmetric, so it holds for the caller/callee pair and for anyone added later.
 */
export function shouldOffer(myId, peerId) {
  return String(myId) < String(peerId);
}

/** The record the caller posts when a call ends, from how it ended. */
export function outcomeStatus({ wasLive, reason }) {
  if (wasLive) return 'done';
  if (reason === 'noanswer') return 'noanswer';
  if (reason === 'declined') return 'declined';
  return 'cancelled';
}

/** The words on the sender's own screen as the call closes. */
export function endedLabel(status, durationSec) {
  switch (status) {
    case 'done': return `CALL ENDED · ${fmtSeconds(durationSec)}`;
    case 'noanswer': return 'NO ANSWER';
    case 'declined': return 'DECLINED';
    default: return 'CALL CANCELLED';
  }
}

/**
 * Snap a dragged window to the nearest corner inside a box, keeping `margin` off the sides,
 * `top` free under the app header and `bottom` free above the tab bar / composer.
 *   snapCorner({ x, y }, { w, h }, { width, height }, { margin, top, bottom }) → { x, y, corner }
 */
export function snapCorner(pos, size, box, { margin = 12, top = 0, bottom = 0 } = {}) {
  const left = margin;
  const right = Math.max(left, box.width - size.width - margin);
  const topY = top + margin;
  const bottomY = Math.max(topY, box.height - size.height - bottom - margin);
  const toRight = pos.x + size.width / 2 >= box.width / 2;
  const toBottom = pos.y + size.height / 2 >= box.height / 2;
  return { x: toRight ? right : left, y: toBottom ? bottomY : topY, corner: `${toBottom ? 'b' : 't'}${toRight ? 'r' : 'l'}` };
}

/** The position for a stored corner ('tr' | 'tl' | 'br' | 'bl'). */
export function cornerPosition(corner, size, box, insets) {
  const probe = { x: corner.endsWith('r') ? box.width : 0, y: corner.startsWith('b') ? box.height : 0 };
  return snapCorner(probe, size, box, insets);
}

/** Audio level 0..1 → the ring's opacity 0.2 → 1. */
export const ringOpacity = (level) => 0.2 + Math.min(1, Math.max(0, level || 0)) * 0.8;

export const QUICK_REPLIES = ['Can’t talk. I’ll call you back.', 'Text me?'];

export const PERM_COPY = {
  mic: {
    title: 'MICROPHONE',
    body: 'Calls go straight from your phone to theirs. Portal needs your mic to send your voice. Nothing is recorded.',
    cta: 'ALLOW MICROPHONE',
  },
  cam: {
    title: 'MIC + CAMERA',
    body: 'Calls go straight from your phone to theirs. Portal needs your mic and camera to send your voice and picture. Nothing is recorded.',
    cta: 'ALLOW MIC + CAMERA',
  },
  camOnly: {
    title: 'CAMERA',
    body: 'Turning on video sends your picture to the people on the call. Nothing is recorded.',
    cta: 'ALLOW CAMERA',
  },
};
export const PERM_DENIED_TOAST = 'CALLS NEED THE MIC · CHANGE IT IN SETTINGS';
